import type { ReactNode } from 'react';

import { Icon } from '../Icon.js';

/**
 * NO FILES YET (the References empty library's layout, 2026-11-02): the
 * illustration, what Files is for, and — when this person may upload here — the
 * page's one red button. When they may not, the reason in its place and no button.
 */
export function EmptyFiles({ action, note }: { action: ReactNode | null; note: ReactNode | null }) {
  return (
    <div className="ref-empty-library files-empty" data-testid="files-empty">
      <EmptyFilesIllustration />
      <h3 className="ref-empty-title">No files yet</h3>
      <p className="ref-empty-body">
        Keep this production’s agreements, permits, location documents and site photos in one place, where the whole
        team can find them.
      </p>
      {action}
      {note}
      <ul className="files-empty-kinds" aria-label="What belongs here" data-testid="files-empty-kinds">
        <li>
          <span className="files-kind" data-kind="PDF" data-size="row" aria-hidden="true">
            <Icon name="pdf" size={18} strokeWidth={1.5} />
          </span>
          <span>
            <strong>Agreements and permits</strong>
            <span>Signed location agreements, filming permits, notices</span>
          </span>
        </li>
        <li>
          <span className="files-kind" data-kind="SPREADSHEET" data-size="row" aria-hidden="true">
            <Icon name="sheet" size={18} strokeWidth={1.5} />
          </span>
          <span>
            <strong>Plans and schedules</strong>
            <span>Spreadsheets, documents and presentations</span>
          </span>
        </li>
        <li>
          <span className="files-kind" data-kind="IMAGE" data-size="row" aria-hidden="true">
            <Icon name="image" size={18} strokeWidth={1.5} />
          </span>
          <span>
            <strong>Location documentation</strong>
            <span>Site photos, floor plans and scans</span>
          </span>
        </li>
      </ul>
    </div>
  );
}

/** A folder with a permit, a spreadsheet and a site photo — drawn inline, no image request. */
function EmptyFilesIllustration() {
  return (
    <svg
      className="ref-empty-art"
      viewBox="0 0 320 190"
      role="img"
      aria-label="A folder holding a permit, a spreadsheet and a photograph"
      data-testid="files-empty-illustration"
    >
      <ellipse cx="160" cy="106" rx="134" ry="74" fill="#fef3f2" />
      <ellipse cx="206" cy="70" rx="60" ry="38" fill="#fee4e2" />
      {/* sparkle */}
      <g stroke="#d43f3f" strokeWidth="4" strokeLinecap="round">
        <path d="M96 36 104 50" />
        <path d="M82 54 96 60" />
        <path d="M118 24 120 38" />
      </g>
      {/* folder back */}
      <path d="M70 70h62l12 12h106v86H70z" fill="#eaecf0" />
      {/* spreadsheet */}
      <g transform="rotate(-7 128 96)">
        <rect x="88" y="52" width="84" height="100" fill="#ffffff" stroke="#d0d5dd" strokeWidth="2" />
        <rect x="98" y="66" width="64" height="70" fill="none" stroke="#98a2b3" strokeWidth="2" />
        <path d="M98 83h64M98 100h64M98 117h64M119 66v70M140 66v70" stroke="#98a2b3" strokeWidth="2" />
        <rect x="98" y="66" width="21" height="17" fill="#d1fadf" />
      </g>
      {/* permit */}
      <g transform="rotate(6 206 96)">
        <rect x="166" y="44" width="84" height="104" fill="#ffffff" stroke="#d0d5dd" strokeWidth="2" />
        <rect x="178" y="60" width="44" height="6" fill="#344054" />
        <rect x="178" y="76" width="60" height="5" fill="#d0d5dd" />
        <rect x="178" y="88" width="54" height="5" fill="#d0d5dd" />
        <rect x="178" y="100" width="58" height="5" fill="#d0d5dd" />
        <circle cx="226" cy="128" r="11" fill="none" stroke="#d43f3f" strokeWidth="3" />
        <path d="M220 128l4 4 8-8" stroke="#d43f3f" strokeWidth="3" fill="none" strokeLinecap="round" />
      </g>
      {/* folder front */}
      <path d="M62 98h196l-10 74H72z" fill="#ffffff" stroke="#d0d5dd" strokeWidth="2" />
      {/* site photo clipped to the front */}
      <g transform="rotate(-4 120 140)">
        <rect x="96" y="112" width="58" height="44" fill="#ffffff" stroke="#d0d5dd" strokeWidth="2" />
        <rect x="100" y="116" width="50" height="32" fill="#cfe3f3" />
        <path d="M100 140c10-8 18-6 26-12s16-8 24-4v24h-50z" fill="#5f8f5a" />
      </g>
      <rect x="180" y="132" width="52" height="6" fill="#eaecf0" />
      <rect x="180" y="144" width="36" height="6" fill="#eaecf0" />
    </svg>
  );
}
